import { LitElement, css, html } from 'lit'

import './ct-program-card.js'
import './ct-catalog-term-card.js'

import {
  calendarPresets,
  programPresets,
} from '../../data/catalog-options.js'
import { getCatalogPrograms } from '../../services/catalog-programs.js'
import {
  getSeasonFromStartDate,
} from '../../utils/format-date.js'
import { formatTermCount } from '../../utils/polish-plurals.js'

const monthHeadingFormatter = new Intl.DateTimeFormat('pl-PL', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

const filterDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

const FILTER_GROUPS = [
  {
    id: 'direction',
    title: 'Kierunek',
    options: ['Europa', 'Azja', 'Afryka', 'Ameryka Północna', 'Ameryka Południowa'],
  },
  {
    id: 'trip-type',
    title: 'Charakter podróży',
    options: ['City break', 'Wypoczynek', 'Wyjazd objazdowy', 'Egzotyka', 'Aktywnie'],
  },
  {
    id: 'audience',
    title: 'Dla kogo',
    selection: 'single',
    options: ['Tylko dla kobiet', 'Dla kobiet i mężczyzn'],
  },
  {
    id: 'duration',
    title: 'Długość',
    options: ['Do 5 dni', '6–9 dni', '10 dni i więcej'],
  },
  {
    id: 'price',
    title: 'Cena',
    options: ['Do 2 999 zł', '3 000–4 999 zł', '5 000–8 000 zł', 'Powyżej 8 000 zł'],
  },
  {
    id: 'availability',
    title: 'Dostępność',
    options: ['Dostępne miejsca', 'Ostatnie miejsca'],
  },
]

const PROGRAM_SORT_OPTIONS = [
  { value: 'recommended', label: 'Polecane' },
  { value: 'price-low', label: 'Cena: od najniższej' },
  { value: 'price-high', label: 'Cena: od najwyższej' },
  { value: 'shortest', label: 'Najkrótsze' },
  { value: 'longest', label: 'Najdłuższe' },
  { value: 'nearest', label: 'Najbliższe' },
  { value: 'recently-added', label: 'Ostatnio dodane' },
]

const TERM_SORT_OPTIONS = [
  { value: 'chronological', label: 'Od najbliższych' },
  { value: 'latest', label: 'Od najdalszych' },
  { value: 'price-low', label: 'Cena: od najniższej' },
  { value: 'price-high', label: 'Cena: od najwyższej' },
  { value: 'shortest', label: 'Najkrótsze' },
  { value: 'longest', label: 'Najdłuższe' },
  { value: 'recently-added', label: 'Ostatnio dodane' },
]

const MONTH_OPTIONS = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
]

function formatMonthHeading(monthKey) {
  const [year, month] = monthKey.split('-').map(Number)
  const label = monthHeadingFormatter.format(new Date(Date.UTC(year, month - 1, 1)))
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function formatSelectedMonth(monthValue) {
  if (!monthValue) return ''
  const [year, month] = monthValue.split('-').map(Number)
  const label = monthHeadingFormatter.format(new Date(Date.UTC(year, month - 1, 1)))
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function formatFilterDate(dateValue) {
  if (!dateValue) return ''
  const [year, month, day] = dateValue.split('-').map(Number)
  return filterDateFormatter.format(new Date(Date.UTC(year, month - 1, day)))
}

function formatResultCount(count) {
  const lastTwoDigits = count % 100
  const lastDigit = count % 10
  const noun = count === 1
    ? 'wynik'
    : lastDigit >= 2 && lastDigit <= 4 && !(lastTwoDigits >= 12 && lastTwoDigits <= 14)
      ? 'wyniki'
      : 'wyników'

  return `${count} ${noun}`
}

export class CtOfferCatalog extends LitElement {
  static properties = {
    activeView: { state: true },
    activePreset: { state: true },
    selectedFilters: { state: true },
    filtersOpen: { state: true },
    sortOpen: { state: true },
    sortValue: { state: true },
    visibleFilterCount: { state: true },
    dateFilterMode: { state: true },
    filterMonth: { state: true },
    filterDateFrom: { state: true },
    filterDateTo: { state: true },
    draftFilters: { state: true },
    draftDateFilterMode: { state: true },
    draftFilterMonth: { state: true },
    draftFilterDateFrom: { state: true },
    draftFilterDateTo: { state: true },
    monthPickerOpen: { state: true },
    monthPickerYear: { state: true },
    programs: { state: true },
    catalogLoading: { state: true },
    catalogError: { state: true },
  }

  constructor() {
    super()
    this.activeView = 'calendar'
    this.activePreset = ''
    this.selectedFilters = []
    this.filtersOpen = false
    this.sortOpen = false
    this.sortValue = 'chronological'
    this.visibleFilterCount = 0
    this.dateFilterMode = 'month'
    this.filterMonth = ''
    this.filterDateFrom = ''
    this.filterDateTo = ''
    this.draftFilters = []
    this.draftDateFilterMode = 'month'
    this.draftFilterMonth = ''
    this.draftFilterDateFrom = ''
    this.draftFilterDateTo = ''
    this.draftDateFilterLabel = ''
    this.monthPickerOpen = false
    this.monthPickerYear = new Date().getFullYear()
    this.programs = []
    this.catalogLoading = true
    this.catalogError = ''
    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this)
    this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this)
    this.measureVisibleFilters = this.measureVisibleFilters.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener('pointerdown', this.handleDocumentPointerDown)
    document.addEventListener('keydown', this.handleDocumentKeyDown)
    this.loadCatalog()
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown)
    document.removeEventListener('keydown', this.handleDocumentKeyDown)
    this.toolbarResizeObserver?.disconnect()
    this.restorePageScroll()
    super.disconnectedCallback()
  }

  firstUpdated() {
    this.toolbarResizeObserver = new ResizeObserver(this.measureVisibleFilters)
    const chips = this.renderRoot.querySelector('.chips')
    if (chips) this.toolbarResizeObserver.observe(chips)
    this.measureVisibleFilters()
  }

  updated(changedProperties) {
    if (
      changedProperties.has('selectedFilters') ||
      changedProperties.has('activeView')
    ) {
      requestAnimationFrame(this.measureVisibleFilters)
    }

    if (changedProperties.has('filtersOpen') || changedProperties.has('sortOpen')) {
      this.updatePageScrollLock()
    }
  }

  static styles = css`
    :host {
      --ct-catalog-body-size: 18px;
      --ct-catalog-body-line: 28px;
      --ct-catalog-h2-size: 34px;
      --ct-catalog-cta-size: 20px;
      display: block;
      width: 100%;
      color: #33404c;
      font-family: Poppins, Arial, sans-serif;
    }

    * {
      box-sizing: border-box;
      -webkit-tap-highlight-color: transparent;
    }

    button { font: inherit; }

    .catalog {
      width: min(100%, 1200px);
      margin: 0 auto;
      padding: 0 0 48px;
    }

    @media (max-width: 1199px) {
      .catalog { width: 94%; }
    }

    .view-switch {
      display: inline-flex;
      gap: 4px;
      margin: 0 0 98px;
      padding: 4px;
      border-radius: 12px;
      background: #ffffff;
      box-shadow: 0 2px 6px rgba(0,0,0,.06);
    }

    .view-button {
      display: inline-flex;
      min-width: 205px;
      min-height: 60px;
      align-items: center;
      justify-content: flex-start;
      gap: 10px;
      padding: 0 14px 0 10px;
      border: 1px solid #dbe5e4;
      border-radius: 12px;
      background: #fff;
      color: #58676c;
      cursor: pointer;
      text-align: left;
      transition: background 160ms ease, color 160ms ease, box-shadow 160ms ease;
    }

    .view-button[aria-selected='true'] {
      background: #fff;
      border-color: #f7931e;
      color: #d9770a;
      box-shadow: none;
    }

    .view-button:focus-visible,
    .preset:focus-visible,
    .filter-button:focus-visible,
    .sort-button:focus-visible,
    .chip-remove:focus-visible,
    .filter-choice:focus-visible,
    .date-mode button:focus-visible,
    .date-field input:focus-visible,
    .month-picker-trigger:focus-visible,
    .month-picker-clear:focus-visible,
    .month-picker-header button:focus-visible,
    .month-picker-grid button:focus-visible,
    .panel-close:focus-visible,
    .panel-primary:focus-visible,
    .panel-secondary:focus-visible,
    .sort-option:focus-visible {
      outline: 3px solid rgba(24,88,106,.25);
      outline-offset: 3px;
    }

    .view-icon {
      width: 36px;
      height: 36px;
      display: grid;
      flex: 0 0 36px;
      place-items: center;
      color: #526a70;
    }

    .view-icon svg {
      width: 100%;
      height: 100%;
      display: block;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.7;
    }

    .view-icon .program-photo-pin-icon {
      width: calc(100% - 2px);
      height: calc(100% - 2px);
      margin: 1px;
      stroke: currentColor;
      stroke-width: 1.275;
    }

    .view-button[aria-selected='true'] .view-icon {
      color: #d9770a;
    }

    .view-copy {
      display: grid;
      gap: 2px;
    }

    .view-copy strong {
      font-size: 18px;
      font-weight: 600;
      line-height: 24px;
    }

    .view-copy small {
      color: #7c898d;
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
    }

    .view-button[aria-selected='true'] .view-copy small {
      color: #8f6b43;
    }

    .quick-section { margin-bottom: 18px; }

    .quick-title {
      margin: 0 0 8px 4px;
      color: #52666c;
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
    }

    .quick-heading {
      margin-bottom: 16px;
    }

    .quick-heading h2 {
      margin: 0;
      color: #2c4249;
      font-family: Poppins, Arial, sans-serif;
      font-size: var(--ct-catalog-h2-size);
      font-weight: 500;
      line-height: 1.2;
    }

    .quick-heading p {
      max-width: 760px;
      margin: 6px 0 0;
      color: #758287;
      font-family: Georgia, 'Times New Roman', serif;
      font-size: var(--ct-catalog-body-size);
      line-height: var(--ct-catalog-body-line);
    }

    .presets {
      display: grid;
      grid-template-columns: repeat(var(--preset-count, 6), minmax(0, 1fr));
      gap: 10px;
      scrollbar-width: none;
    }

    .presets::-webkit-scrollbar { display: none; }

    .preset {
      display: flex;
      min-height: 48px;
      align-items: center;
      gap: 6px;
      padding: 10px 12px;
      border: 1px solid #dbe5e4;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0,0,0,.03);
      color: #33494f;
      cursor: pointer;
      text-align: left;
      transition: border-color 160ms ease, background 160ms ease, color 160ms ease, box-shadow 160ms ease;
    }

    @media (hover: hover) and (pointer: fine) {
      .preset:hover {
        border-color: #9ab8ba;
        background: #f8fbfa;
      }
    }

    .preset[aria-pressed='true'] {
      border-color: #f7931e;
      background: #fff;
      color: #d9770a;
    }

    .preset-icon {
      position: relative;
      width: 30px;
      height: 30px;
      display: grid;
      flex: 0 0 30px;
      place-items: center;
      color: #18586a;
    }

    .preset[aria-pressed='true'] .preset-icon {
      color: inherit;
    }

    .preset-icon svg {
      width: 21px;
      height: 21px;
      display: block;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.7;
    }

    .preset-icon .preset-active-compass {
      transform: scale(1.06);
      transform-origin: center;
    }

    .preset-icon .preset-women-icon {
      stroke-width: 1.5;
    }

    .preset-icon .preset-landmark-icon {
      stroke-width: 1.25;
    }

    .preset-icon .preset-luggage-icon {
      stroke-width: 1.25;
    }

    .preset-custom-icon {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      display: block;
      border-radius: inherit;
      object-fit: contain;
    }

    .preset-label {
      display: flex;
      min-width: 0;
      align-self: stretch;
      align-items: center;
      font-size: 15px;
      font-weight: 600;
      line-height: 18px;
    }

    .toolbar-wrap { position: relative; margin-bottom: 40px; }

    .toolbar {
      position: relative;
      z-index: 22;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 14px;
      min-height: 68px;
      padding: 12px 17px 12px 18px;
      border: 1px solid #e0e7e7;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 2px 6px rgba(0,0,0,.06);
    }

    .filter-area {
      display: flex;
      min-width: 0;
      align-items: center;
      gap: 9px;
    }

    .filter-button {
      display: inline-flex;
      min-height: 42px;
      flex: 0 0 auto;
      align-items: center;
      gap: 8px;
      padding: 0 15px 0 12px;
      border: 1px solid #cfdada;
      border-radius: 12px;
      background: #fff;
      color: #33494f;
      cursor: pointer;
      font-size: 15px;
      font-weight: 600;
    }

    .filter-button[data-has-filters='false'] {
      padding-right: 19px;
    }

    .filter-button-icon,
    .sort-icon {
      width: 19px;
      height: 19px;
      display: grid;
      flex: 0 0 19px;
      place-items: center;
      color: #18586a;
    }

    .filter-button-icon svg,
    .sort-icon svg,
    .sort-chevron {
      width: 100%;
      height: 100%;
      display: block;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.8;
    }

    .filter-count {
      display: grid;
      min-width: 20px;
      height: 20px;
      place-items: center;
      border-radius: 50%;
      background: #18586a;
      color: #fff;
      font-size: 12px;
    }

    .chips {
      position: relative;
      display: flex;
      min-width: 0;
      flex: 1 1 auto;
      align-items: center;
      gap: 7px;
      overflow: hidden;
    }

    .chip-measurer {
      position: fixed;
      top: -9999px;
      left: -9999px;
      display: flex;
      gap: 7px;
      visibility: hidden;
      pointer-events: none;
    }

    .chip {
      display: inline-flex;
      min-height: 35px;
      flex: 0 0 auto;
      align-items: center;
      gap: 6px;
      padding: 0 7px 0 13px;
      border-radius: 12px;
      background: #edf4f3;
      color: #38565d;
      font-size: 14px;
      font-weight: 600;
    }

    .chip-remove {
      display: grid;
      width: 22px;
      height: 22px;
      place-items: center;
      border: 0;
      border-radius: 50%;
      background: transparent;
      color: #5e7277;
      cursor: pointer;
    }

    .chip--more {
      padding: 0 7px 0 13px;
      color: #18586a;
    }

    .sort-button {
      position: relative;
      display: flex;
      min-height: 42px;
      align-items: center;
      gap: 7px;
      padding: 0 38px 0 12px;
      border: 1px solid #cfdada;
      border-radius: 12px;
      background: #fff;
      color: #33494f;
      cursor: pointer;
      text-align: left;
    }

    .sort-label {
      display: inline-flex;
      align-items: center;
      color: #52666c;
      font-size: 13px;
      font-weight: 600;
      line-height: 20px;
      transform: translateY(1px);
      white-space: nowrap;
    }

    .sort-value {
      display: inline-flex;
      align-items: center;
      min-width: 112px;
      color: #33494f;
      font-size: 15px;
      font-weight: 600;
      line-height: 20px;
      white-space: nowrap;
    }

    .filter-button[aria-expanded='true'],
    .sort-button[aria-expanded='true'] {
      border-color: #f7931e;
    }

    .sort-chevron {
      position: absolute;
      right: 12px;
      width: 14px;
      height: 14px;
      pointer-events: none;
    }

    .panel-backdrop {
      position: fixed;
      z-index: 20;
      inset: 0;
      border: 0;
      background: transparent;
      cursor: default;
      pointer-events: auto;
    }

    .filter-panel,
    .sort-panel {
      position: absolute;
      z-index: 23;
      top: calc(100% + 10px);
      border: 1px solid #dbe4e4;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 3px 9px rgba(0,0,0,.12);
      animation: panel-in 180ms ease-out both;
    }

    .filter-panel {
      right: 0;
      left: 0;
      display: grid;
      width: 100%;
      max-height: min(70dvh, 650px);
      grid-template-rows: auto minmax(0, 1fr) auto;
      overflow: hidden;
    }

    .sort-panel {
      right: 0;
      width: min(320px, calc(100% - 24px));
      padding: 8px;
    }

    .panel-header {
      display: flex;
      min-height: 70px;
      align-items: center;
      gap: 16px;
      padding: 14px 20px;
      border-bottom: 1px solid #e6ecec;
    }

    .panel-heading-copy { min-width: 0; }

    .panel-eyebrow {
      margin: 0 0 1px;
      color: #d9770a;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: .08em;
      line-height: 16px;
      text-transform: uppercase;
    }

    .filter-panel-title,
    .sort-panel-title {
      margin: 0;
      color: #2e444b;
      font-size: 19px;
      font-weight: 600;
      line-height: 26px;
    }

    .panel-close {
      display: grid;
      width: 36px;
      height: 36px;
      flex: 0 0 36px;
      place-items: center;
      border: 1px solid #dbe4e4;
      border-radius: 12px;
      background: #fff;
      color: #52666c;
      cursor: pointer;
      font-size: 24px;
      font-weight: 300;
      line-height: 1;
    }

    .filter-panel-scroll {
      width: 100%;
      min-width: 0;
      min-height: 0;
      padding: 16px 18px;
      overflow-x: hidden;
      overflow-y: auto;
      overscroll-behavior-x: none;
      overscroll-behavior-y: contain;
      touch-action: pan-y;
      -webkit-overflow-scrolling: touch;
    }

    .filter-groups-grid {
      display: grid;
      min-width: 0;
      grid-template-columns: minmax(0, 1fr);
      gap: 14px;
    }

    .filter-groups-grid .filter-group {
      display: block;
      min-width: 0;
    }

    .filter-group--date {
      margin-bottom: 14px;
      padding-bottom: 0;
    }

    .filter-group-heading {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }

    .filter-group--date .filter-group-heading { margin-bottom: 10px; }

    .filter-group-heading::before {
      width: 3px;
      height: 18px;
      flex: 0 0 3px;
      border-radius: 3px;
      background: #f7931e;
      content: '';
    }

    .filter-group-heading h4 {
      margin: 0;
      color: #31484f;
      font-size: 16px;
      font-weight: 700;
      line-height: 22px;
    }

    .filter-choices {
      display: flex;
      min-width: 0;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px 24px;
    }

    .filter-groups-grid .filter-group-heading {
      min-height: 30px;
      margin-bottom: 6px;
    }

    .filter-choice {
      display: flex;
      min-width: 0;
      min-height: 30px;
      align-items: center;
      gap: 8px;
      padding: 1px 0;
      border: 0;
      background: transparent;
      color: #52666c;
      cursor: pointer;
      font-size: 14px;
      line-height: 20px;
      text-align: left;
    }

    .choice-control {
      display: grid;
      width: 20px;
      height: 20px;
      flex: 0 0 20px;
      place-items: center;
      border: 1px solid #bdcbcb;
      border-radius: 6px;
      background: #fff;
      color: #fff;
    }

    .filter-choice[role='radio'] .choice-control { border-radius: 50%; }

    .filter-choice[aria-checked='true'] {
      color: #18586a;
      font-weight: 600;
    }

    .filter-choice[aria-checked='true'] .choice-control {
      border-color: #18586a;
      background: #18586a;
    }

    .filter-choice[role='radio'][aria-checked='true'] .choice-control {
      border: 5px solid #18586a;
      background: #fff;
    }

    .choice-control svg {
      width: 13px;
      height: 13px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 2;
    }

    .date-filter-layout {
      display: grid;
      grid-template-columns: auto minmax(240px, 1fr);
      align-items: end;
      gap: 14px;
    }

    .date-mode {
      display: inline-grid;
      grid-template-columns: repeat(2, auto);
      padding: 3px;
      border: 1px solid #dbe4e4;
      border-radius: 12px;
      background: #f3f6f6;
    }

    .date-mode button {
      min-height: 38px;
      padding: 0 15px;
      border: 0;
      border-radius: 9px;
      background: transparent;
      color: #65767b;
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
    }

    .date-mode button[aria-pressed='true'] {
      background: #fff;
      color: #18586a;
      box-shadow: 0 1px 4px rgba(0,0,0,.08);
    }

    .date-range {
      display: grid;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }

    .date-field {
      display: grid;
      min-width: 0;
      gap: 4px;
      color: #718085;
      font-size: 11px;
      font-weight: 600;
      line-height: 16px;
    }

    .date-field input {
      width: 100%;
      min-width: 0;
      max-width: 100%;
      min-height: 44px;
      padding: 0 12px;
      border: 1px solid #cfdada;
      border-radius: 12px;
      background: #fff;
      color: #33494f;
      font: 500 14px/20px Poppins, Arial, sans-serif;
    }

    .date-field input[type='date'],
    .date-field input[type='month'] {
      min-inline-size: 0;
    }

    .date-field input[type='date']::-webkit-date-and-time-value,
    .date-field input[type='date']::-webkit-datetime-edit,
    .date-field input[type='month']::-webkit-date-and-time-value,
    .date-field input[type='month']::-webkit-datetime-edit {
      min-width: 0;
      padding: 0;
      text-align: left;
    }

    .date-field--inside {
      position: relative;
      display: block;
    }

    .date-field--inside > span {
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 12px;
      color: #718085;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      pointer-events: none;
      transform: translateY(-50%);
    }

    .date-field--inside input { padding-left: 40px; }

    .month-picker-native {
      position: relative;
      display: none;
    }

    .month-picker-custom {
      position: relative;
      display: block;
    }

    .native-month-label {
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 12px;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      color: #849195;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      pointer-events: none;
      transform: translateY(-50%);
    }

    .native-month-label svg {
      width: 18px;
      height: 18px;
      flex: 0 0 18px;
      fill: none;
      stroke: #18586a;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.7;
    }

    .month-picker-native[data-has-value='false'] input,
    .month-picker-native[data-has-value='false'] input::-webkit-datetime-edit {
      color: transparent;
    }

    .month-picker-control { position: relative; }

    .month-picker-trigger {
      display: grid;
      width: 100%;
      min-height: 44px;
      grid-template-columns: 19px minmax(0, 1fr) 14px;
      align-items: center;
      gap: 9px;
      padding: 0 12px;
      border: 1px solid #cfdada;
      border-radius: 12px;
      background: #fff;
      color: #33494f;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      text-align: left;
    }

    .month-picker-trigger[aria-expanded='true'] { border-color: #f7931e; }

    .month-picker-control[data-has-value='true'] .month-picker-trigger {
      grid-template-columns: 19px minmax(0, 1fr) 26px 14px;
    }

    .month-picker-trigger svg {
      width: 19px;
      height: 19px;
      fill: none;
      stroke: #18586a;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.7;
    }

    .month-picker-trigger .month-picker-chevron {
      width: 14px;
      height: 14px;
      stroke: #66777c;
    }

    .month-placeholder { color: #849195; }

    .month-clear-space { width: 26px; height: 26px; }

    .month-picker-clear {
      position: absolute;
      top: 50%;
      right: 31px;
      display: grid;
      width: 26px;
      height: 26px;
      place-items: center;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: transparent;
      color: #718085;
      cursor: pointer;
      font-size: 19px;
      font-weight: 400;
      line-height: 1;
      transform: translateY(-50%);
    }

    .month-picker-clear:hover {
      background: #edf2f2;
      color: #33494f;
    }

    .month-picker-popover {
      position: absolute;
      z-index: 4;
      top: calc(100% + 6px);
      right: 0;
      width: 320px;
      max-width: calc(100vw - 64px);
      padding: 10px;
      border: 1px solid #dbe4e4;
      border-radius: 12px;
      background: #fff;
      box-shadow: 0 3px 9px rgba(0,0,0,.12);
      animation: panel-in 150ms ease-out both;
    }

    .month-picker-header {
      display: grid;
      grid-template-columns: 36px 1fr 36px;
      align-items: center;
      margin-bottom: 7px;
    }

    .month-picker-header strong {
      color: #31484f;
      font-size: 15px;
      font-weight: 700;
      text-align: center;
    }

    .month-picker-header button {
      display: grid;
      width: 34px;
      height: 34px;
      place-items: center;
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: #52666c;
      cursor: pointer;
      font-size: 25px;
      line-height: 1;
    }

    .month-picker-header button:not(:disabled):hover { background: #f3f6f6; }

    .month-picker-header button:disabled {
      color: #b9c2c4;
      cursor: not-allowed;
      opacity: .55;
    }

    .month-picker-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 4px;
    }

    .month-picker-grid button {
      min-height: 36px;
      padding: 0 5px;
      border: 1px solid transparent;
      border-radius: 8px;
      background: #fff;
      color: #52666c;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
    }

    .month-picker-grid button:not(:disabled):hover { background: #f3f6f6; }

    .month-picker-grid button:disabled {
      background: #f8f9f9;
      color: #aab4b7;
      cursor: not-allowed;
      opacity: .6;
    }

    .month-picker-grid button[aria-pressed='true'] {
      border-color: #f7931e;
      background: #fff8ef;
      color: #c96f0b;
      font-weight: 700;
    }

    .panel-footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      padding: 12px 20px;
      border-top: 1px solid #e6ecec;
      background: #fff;
    }

    .panel-secondary,
    .panel-primary {
      min-height: 44px;
      padding: 0 20px;
      border-radius: 12px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .panel-secondary {
      border: 1px solid #cfdada;
      background: #fff;
      color: #52666c;
    }

    .panel-secondary:disabled {
      border-color: #e2e7e7;
      background: #f7f9f9;
      color: #a0aaad;
      cursor: not-allowed;
    }

    .panel-primary {
      min-width: 176px;
      border: 1px solid #f7931e;
      background: #f7931e;
      color: #fff;
      text-transform: uppercase;
    }

    .panel-primary:disabled {
      border-color: #dbe2e2;
      background: #dbe2e2;
      color: #7b888c;
      cursor: not-allowed;
    }

    .sort-panel .panel-header {
      min-height: 48px;
      padding: 4px 8px 10px;
    }

    .sort-panel .panel-close {
      margin-left: auto;
      width: 34px;
      height: 34px;
      flex-basis: 34px;
    }

    .filter-panel .panel-close { margin-left: auto; }

    .filter-panel .panel-header {
      min-height: 58px;
      padding: 8px 18px;
    }

    .filter-panel .panel-footer { padding: 10px 18px; }

    .sort-options { display: grid; gap: 0; padding-top: 8px; }

    .sort-option {
      display: flex;
      width: 100%;
      min-height: 44px;
      align-items: center;
      gap: 10px;
      padding: 0 10px;
      border: 0;
      border-radius: 10px;
      background: #fff;
      color: #52666c;
      cursor: pointer;
      font-size: 16px;
      line-height: 20px;
      text-align: left;
    }

    .sort-option:hover { background: #f4f7f7; }

    .sort-radio {
      width: 18px;
      height: 18px;
      flex: 0 0 18px;
      border: 1px solid #bdcbcb;
      border-radius: 50%;
    }

    .sort-option[aria-selected='true'] {
      background: #edf5f4;
      color: #18586a;
      font-weight: 600;
    }

    .sort-option[aria-selected='true'] .sort-radio {
      border: 5px solid #18586a;
    }

    @keyframes panel-in {
      from { opacity: 0; transform: translateY(-6px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .results-heading {
      margin-bottom: 32px;
    }

    .results-heading--calendar { margin-bottom: 24px; }

    .results-heading--offset { padding-top: 8px; }

.results-heading h2 {
  display: flex;
  align-items: baseline;
  margin: 0;
  color: #404040;
  font-family: Poppins, Arial, sans-serif;
  font-size: var(--ct-catalog-h2-size);
  font-weight: 500;
  line-height: 1.2;
}

.results-heading h2::before {
  width: 5px;
  height: calc(.95em + 8px);
  align-self: center;
  flex: 0 0 5px;
  margin-right: 16px;
  border-radius: 0;
  background: #f7931e;
  content: '';
}

    .results-count {
      position: relative;
      margin-left: 12px;
      padding-left: 13px;
      color: #829095;
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
    }

    .results-count::before {
      position: absolute;
      top: 50%;
      left: 0;
      width: 1px;
      height: 20px;
      background: #d8e1e1;
      content: '';
      transform: translateY(-50%);
    }

    .results-heading p {
      max-width: 760px;
      margin: 6px 0 0;
      color: #758287;
      font-family: Georgia, 'Times New Roman', serif;
      font-size: var(--ct-catalog-body-size);
      line-height: var(--ct-catalog-body-line);
    }

    .program-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 24px;
    }

    .catalog-status {
      margin: 0 0 20px;
      color: #66777c;
      font-size: 15px;
      line-height: 22px;
    }

    .catalog-status--warning { color: #8a5b25; }

    ct-program-card { max-width: 390px; }

    .month-group { margin-top: 34px; }
    .month-group:first-of-type { margin-top: 0; }

    .month-heading {
      display: flex;
      align-items: baseline;
      gap: 0;
      margin: 0 0 8px;
      color: #2c4249;
      font-size: 24px;
      font-weight: 500;
      line-height: 32px;
    }

    .month-count {
      position: relative;
      margin-left: 12px;
      padding-left: 13px;
      color: #829095;
      font-size: 14px;
      font-weight: 600;
      line-height: 22px;
    }

    .month-count::before {
      position: absolute;
      top: 50%;
      left: 0;
      width: 1px;
      height: 18px;
      background: #d8e1e1;
      content: '';
      transform: translateY(-50%);
    }

    .term-list { display: grid; gap: 12px; }

    @media (min-width: 1200px) {
      .toolbar { padding: 12px; }
    }

    @media (min-width: 768px) and (max-width: 1199px) {
      .program-grid {
        grid-template-columns: repeat(2, minmax(0, 480px));
        justify-content: start;
      }
      ct-program-card { width: 100%; max-width: 480px; }
    }

    @media (max-width: 767px) {
      .program-grid {
        grid-template-columns: minmax(0, 480px);
        justify-content: start;
      }
      ct-program-card { width: 100%; max-width: 480px; }
    }

    @media (min-width: 768px) and (max-width: 1199px) {
      :host {
        --ct-catalog-body-size: 17px;
        --ct-catalog-body-line: 26px;
        --ct-catalog-h2-size: 30px;
        --ct-catalog-cta-size: 18px;
      }
      .view-copy strong { font-size: 17px; }
      .view-copy small { font-size: 11px; }
      .toolbar { padding: 12px; }
      .quick-title { font-size: 15px; line-height: 21px; }
      .presets { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .preset { min-height: 56px; padding: 8px 10px; }
      .preset-label { font-size: 14px; }
      .chip { font-size: 13px; }
      .sort-label { font-size: 12px; }
      .sort-value { font-size: 14px; }
      .results-count { font-size: 15px; }
      .panel-backdrop { background: rgba(31, 45, 49, .12); }
      .filter-panel {
        top: calc(100% + 10px);
        max-height: min(74dvh, 650px);
      }
      .filter-panel-scroll { padding: 15px 16px; }
      .filter-groups-grid { gap: 14px; }
      .filter-choices { gap: 3px 20px; }
      .filter-choice { min-height: 32px; }
      .date-filter-layout { grid-template-columns: auto minmax(210px, 1fr); }
    }

    @media (max-width: 767px) {
      .results-heading { margin-bottom: 24px; }
      .quick-section { margin-bottom: 14px; }
      .quick-title { margin-bottom: 8px; font-size: 14px; line-height: 20px; }
      :host {
        --ct-catalog-body-size: 16px;
        --ct-catalog-body-line: 24px;
        --ct-catalog-h2-size: 26px;
        --ct-catalog-cta-size: 18px;
      }
      .view-copy strong { font-size: clamp(14px, calc(12.4px + 1vw), 18px); line-height: clamp(19px, calc(17.4px + .47vw), 21px); }
      .view-copy small { font-size: clamp(9px, calc(7px + .8vw), 14px); line-height: clamp(11px, calc(8.6px + .7vw), 14px); }
      .presets {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 4px;
        margin-right: 0;
        padding: 0;
        overflow: visible;
      }
      .preset {
        width: 100%;
        min-width: 0;
        min-height: clamp(38px, calc(38px + .64vw), 46px);
        gap: clamp(2px, calc(1.2px + .24vw), 3px);
        padding: 5px clamp(5px, calc(3.4px + .47vw), 7px);
      }
      .preset-icon { width: clamp(20px, calc(18.4px + .47vw), 22px); height: clamp(20px, calc(18.4px + .47vw), 22px); flex-basis: clamp(20px, calc(18.4px + .47vw), 22px); }
      .preset-icon svg { width: clamp(17px, calc(16.2px + .24vw), 18px); height: clamp(17px, calc(16.2px + .24vw), 18px); }
      .preset-label { min-width: 0; font-size: clamp(11px, calc(8.6px + 1vw), 14px); white-space: nowrap; }
      .filter-button { font-size: clamp(13px, calc(12.2px + .24vw), 14px); }
      .filter-count { font-size: 11px; }
      .chip { font-size: 13px; }
      .sort-label { display: none; }
      .sort-value { min-width: 0; font-size: clamp(13px, calc(12.2px + .24vw), 14px); }
      .results-count { font-size: 14px; }
      .month-heading { font-size: 20px; line-height: 28px; }
      .month-count { font-size: 13px; line-height: 20px; }

      .view-switch {
        display: flex;
        width: min(460px, 100%);
        max-width: 100%;
        margin-right: auto;
        margin-bottom: 32px;
        margin-left: auto;
      }

      .view-button {
        min-width: 0;
        flex: 1 1 0;
        min-height: 58px;
        gap: clamp(6px, calc(2.8px + .94vw), 10px);
        padding-right: clamp(8px, calc(6.4px + .47vw), 10px);
        padding-left: clamp(5px, calc(1px + 1.18vw), 10px);
      }

      .view-copy small { white-space: nowrap; }
      .catalog { padding-bottom: 40px; }
      .view-icon { width: clamp(30px, calc(26.8px + .94vw), 34px); height: clamp(30px, calc(26.8px + .94vw), 34px); flex-basis: clamp(30px, calc(26.8px + .94vw), 34px); }
      .toolbar { padding: 10px; border-radius: 12px; }
      .panel-backdrop {
        background: rgba(31, 45, 49, .28);
        pointer-events: auto;
      }
      .filter-panel,
      .sort-panel {
        position: fixed;
        z-index: 23;
        top: auto;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        max-width: none;
        border-right: 0;
        border-bottom: 0;
        border-left: 0;
        border-radius: 16px 16px 0 0;
        animation: sheet-in 240ms cubic-bezier(.22,.72,.25,1) both;
      }
      .filter-panel {
        height: min(92dvh, 760px);
        max-height: 92dvh;
      }
      .sort-panel {
        max-height: min(76dvh, 600px);
        padding: 8px 10px calc(12px + env(safe-area-inset-bottom));
        overflow: auto;
      }
      .panel-header {
        min-height: 64px;
        padding: 11px 14px;
      }
      .panel-eyebrow { font-size: 10px; }
      .filter-panel-title,
      .sort-panel-title { font-size: 20px; line-height: 24px; }
      .filter-panel .panel-header { min-height: 54px; padding: 6px 14px; }
      .filter-panel-scroll { padding: 12px 14px 16px; }
      .filter-groups-grid { grid-template-columns: 1fr; gap: 20px; }
      .filter-groups-grid .filter-group { display: block; }
      .filter-group--date { margin-bottom: 14px; padding-bottom: 0; }
      .filter-group-heading { margin-bottom: 5px; }
      .filter-groups-grid .filter-group-heading {
        min-height: 0;
        margin-bottom: 5px;
      }
      .filter-group--date .filter-group-heading { margin-bottom: 9px; }
      .filter-group-heading h4 { font-size: 16px; }
      .filter-choices {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0 12px;
      }
      .filter-choice { min-height: 34px; font-size: 14px; }
      .date-filter-layout { grid-template-columns: 1fr; gap: 8px; }
      .date-mode { width: 100%; grid-template-columns: repeat(2, 1fr); }
      .date-mode button { min-height: 44px; }
      .date-range { gap: 8px; }
      .date-field input {
        height: 44px;
        min-height: 44px;
      }
      .date-field input[type='date'],
      .date-field input[type='month'] {
        -webkit-appearance: none;
        appearance: none;
      }
      .date-field--inside input {
        padding-right: 8px;
        padding-left: 38px;
      }
      .month-picker-custom { display: none; }
      .month-picker-native {
        display: block;
        width: 100%;
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        border-radius: 12px;
      }
      .month-picker-native input { padding-left: 40px; }
      .panel-footer {
        padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
      }
      .filter-panel .panel-footer {
        padding: 8px 14px calc(8px + env(safe-area-inset-bottom));
      }
      .panel-secondary,
      .panel-primary { min-height: 46px; padding: 0 14px; }
      .panel-primary { min-width: 0; flex: 1 1 auto; }
      .sort-panel .panel-header { padding: 2px 4px 10px; }
      .sort-option { min-height: 40px; padding: 0 8px; font-size: 15px; }
      ct-program-card { max-width: none; }
      .toolbar {
        z-index: auto;
        grid-template-columns: auto minmax(0, 204px);
        grid-template-rows: auto auto;
        justify-content: space-between;
        row-gap: 10px;
        column-gap: clamp(8px, calc(-24px + 9.4vw), 48px);
      }

      .filter-area { display: contents; }

      .filter-button {
        width: auto;
        min-width: 0;
        grid-column: 1;
        grid-row: 1;
        justify-content: flex-start;
        padding-right: clamp(12px, calc(10.4px + .47vw), 14px);
        padding-left: 10px;
      }

      .filter-button[data-has-filters='false'] { padding-right: clamp(14px, calc(12.4px + .47vw), 16px); }

      .sort-button {
        width: 100%;
        min-width: 0;
        grid-column: 2;
        grid-row: 1;
        padding-left: 10px;
        overflow: hidden;
      }

      .sort-value { min-width: 0; overflow: hidden; text-overflow: ellipsis; }

      .chips {
        width: 100%;
        grid-column: 1 / -1;
        grid-row: 2;
        padding: 2px 0 0;
        overflow: hidden;
      }

      .chips[data-has-filters='false'] { display: none; }
      .toolbar[data-has-filters='false'] { grid-template-rows: auto; row-gap: 0; }
      .view-copy { min-width: 0; gap: 4px; }
      .view-copy strong,
      .view-copy small { white-space: nowrap; }
      .toolbar { padding-right: clamp(10px, calc(6.8px + .94vw), 14px); padding-left: clamp(10px, calc(6.8px + .94vw), 14px); }
      .filter-button { gap: clamp(6px, calc(4.4px + .47vw), 8px); }
      .filter-button-icon { width: clamp(18px, calc(17.2px + .24vw), 19px); height: clamp(18px, calc(17.2px + .24vw), 19px); flex-basis: clamp(18px, calc(17.2px + .24vw), 19px); }
      .sort-button {
        gap: clamp(4px, calc(1.6px + .7vw), 7px);
        padding-right: clamp(27px, calc(18.2px + 2.59vw), 38px);
        padding-left: 10px;
      }
      .sort-icon { width: clamp(17px, calc(15.4px + .47vw), 19px); height: clamp(17px, calc(15.4px + .47vw), 19px); flex-basis: clamp(17px, calc(15.4px + .47vw), 19px); }
      .sort-chevron { right: clamp(8px, calc(4.8px + .94vw), 12px); width: clamp(12px, calc(10.4px + .47vw), 14px); height: clamp(12px, calc(10.4px + .47vw), 14px); }
    }

    @keyframes sheet-in {
      from { opacity: .6; transform: translateY(100%); }
      to { opacity: 1; transform: translateY(0); }
    }

    @media (max-width: 480px) {
      .preset-icon { display: none; }
      .preset { justify-content: center; text-align: center; }
    }

    @media (min-width: 481px) and (max-width: 767px) {
      .preset {
        gap: clamp(4px, calc(3.2px + .24vw), 5px);
        padding-left: clamp(7px, calc(5.4px + .47vw), 9px);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .view-button, .preset { transition: none; }
      .filter-panel, .sort-panel { animation: none; }
    }
  `

  async loadCatalog() {
    if (this.catalogLoadPromise) return this.catalogLoadPromise

    this.catalogLoading = true
    this.catalogError = ''
    this.catalogLoadPromise = getCatalogPrograms()

    try {
      const result = await this.catalogLoadPromise
      this.programs = result.data
      if (!this.filterMonth && !this.draftFilterMonth) {
        this.monthPickerYear = this.getDefaultMonthPickerYear()
      }
      this.catalogError = result.errors.length
        ? 'Nie udało się odświeżyć części ofert. Pokazujemy dostępne programy.'
        : ''
    } catch {
      this.programs = []
      this.catalogError = 'Nie udało się pobrać ofert. Spróbuj ponownie później.'
    } finally {
      this.catalogLoading = false
    }
  }

  changeView(view) {
    this.activeView = view
    this.activePreset = ''
    this.selectedFilters = []
    this.filtersOpen = false
    this.sortOpen = false
    this.dateFilterMode = 'month'
    this.filterMonth = ''
    this.filterDateFrom = ''
    this.filterDateTo = ''
    this.draftFilters = []
    this.draftDateFilterMode = 'month'
    this.draftFilterMonth = ''
    this.draftFilterDateFrom = ''
    this.draftFilterDateTo = ''
    this.draftDateFilterLabel = ''
    this.monthPickerOpen = false
    this.sortValue = view === 'programs'
      ? 'recommended'
      : 'chronological'
  }

  selectPreset(preset) {
    this.activePreset = preset.label
    this.filterMonth = ''
    this.filterDateFrom = ''
    this.filterDateTo = ''
    this.selectedFilters = [...(preset.filters || [])]
  }

  openFilters() {
    this.draftFilters = [...this.selectedFilters]
    this.draftDateFilterMode = this.dateFilterMode
    this.draftFilterMonth = this.filterMonth
    this.draftFilterDateFrom = this.filterDateFrom
    this.draftFilterDateTo = this.filterDateTo
    this.draftDateFilterLabel = this.getDateFilterLabel(
      this.dateFilterMode,
      this.filterMonth,
      this.filterDateFrom,
      this.filterDateTo
    )
    this.monthPickerOpen = false
    this.monthPickerYear = this.filterMonth
      ? Number(this.filterMonth.slice(0, 4))
      : this.getDefaultMonthPickerYear()
    this.filtersOpen = true
    this.sortOpen = false
  }

  toggleFiltersPanel() {
    if (this.filtersOpen) {
      this.filtersOpen = false
      this.monthPickerOpen = false
      return
    }
    this.openFilters()
  }

  toggleFilter(label, group = null) {
    if (group?.selection === 'single') {
      const wasSelected = this.draftFilters.includes(label)
      const withoutGroup = this.draftFilters.filter(
        item => !group.options.includes(item)
      )
      this.draftFilters = wasSelected ? withoutGroup : [...withoutGroup, label]
      return
    }

    this.draftFilters = this.draftFilters.includes(label)
      ? this.draftFilters.filter(item => item !== label)
      : [...this.draftFilters, label]
  }

  removeFilter(label) {
    const appliedDateLabel = this.getDateFilterLabel(
      this.dateFilterMode,
      this.filterMonth,
      this.filterDateFrom,
      this.filterDateTo
    )
    this.selectedFilters = this.selectedFilters.filter(
      item => item !== label
    )
    this.activePreset = ''
    if (label === appliedDateLabel || label.startsWith('Termin:')) {
      this.filterMonth = ''
      this.filterDateFrom = ''
      this.filterDateTo = ''
    }
  }

  clearDraftFilters() {
    this.draftFilters = []
    this.draftDateFilterMode = 'month'
    this.draftFilterMonth = ''
    this.draftFilterDateFrom = ''
    this.draftFilterDateTo = ''
    this.draftDateFilterLabel = ''
    this.monthPickerOpen = false
  }

  setDateFilterMode(mode) {
    if (this.draftDateFilterMode === mode) return
    this.draftDateFilterMode = mode
    this.draftFilterMonth = ''
    this.draftFilterDateFrom = ''
    this.draftFilterDateTo = ''
    this.monthPickerOpen = false
    this.syncDateFilterChip()
  }

  toggleMonthPicker() {
    if (!this.monthPickerOpen && this.draftFilterMonth) {
      this.monthPickerYear = Number(this.draftFilterMonth.slice(0, 4))
    } else if (!this.monthPickerOpen) {
      this.monthPickerYear = this.getDefaultMonthPickerYear()
    }
    this.monthPickerOpen = !this.monthPickerOpen
  }

  getAvailableMonthValues() {
    return new Set(
      this.programs.flatMap(program =>
        program.terms
          .map(term => String(term.startDate || '').slice(0, 7))
          .filter(value => /^\d{4}-\d{2}$/.test(value))
      )
    )
  }

  getAvailableMonthYears() {
    return [...new Set(
      [...this.getAvailableMonthValues()].map(value => Number(value.slice(0, 4)))
    )].sort((first, second) => first - second)
  }

  getDefaultMonthPickerYear() {
    const years = this.getAvailableMonthYears()
    const currentYear = new Date().getFullYear()

    return years.find(year => year >= currentYear) ??
      years.at(-1) ??
      currentYear
  }

  canChangeMonthPickerYear(offset) {
    const years = this.getAvailableMonthYears()
    const currentIndex = years.indexOf(this.monthPickerYear)

    return currentIndex >= 0 &&
      currentIndex + offset >= 0 &&
      currentIndex + offset < years.length
  }

  changeMonthPickerYear(offset) {
    if (!this.canChangeMonthPickerYear(offset)) return

    const years = this.getAvailableMonthYears()
    const currentIndex = years.indexOf(this.monthPickerYear)
    this.monthPickerYear = years[currentIndex + offset]
  }

  selectMonth(monthIndex) {
    const month = String(monthIndex + 1).padStart(2, '0')
    const value = `${this.monthPickerYear}-${month}`

    if (!this.getAvailableMonthValues().has(value)) return

    this.draftFilterMonth = value
    this.monthPickerOpen = false
    this.syncDateFilterChip()
  }

  clearMonthSelection() {
    this.draftFilterMonth = ''
    this.monthPickerOpen = false
    this.syncDateFilterChip()
  }

  updateDateFilter(field, value) {
    this[field] = value
    this.syncDateFilterChip()
  }

  getDateFilterLabel(mode, month, from, to) {
    if (mode === 'month') {
      return month ? formatSelectedMonth(month) : ''
    }

    if (from && to) {
      return `Od: ${formatFilterDate(from)} – Do: ${formatFilterDate(to)}`
    }
    if (from) return `Od: ${formatFilterDate(from)}`
    if (to) return `Do: ${formatFilterDate(to)}`
    return ''
  }

  syncDateFilterChip() {
    const filtersWithoutDate = this.draftFilters.filter(
      label => label !== this.draftDateFilterLabel && !label.startsWith('Termin:')
    )
    const dateLabel = this.getDateFilterLabel(
      this.draftDateFilterMode,
      this.draftFilterMonth,
      this.draftFilterDateFrom,
      this.draftFilterDateTo
    )

    this.draftDateFilterLabel = dateLabel
    this.draftFilters = dateLabel
      ? [...filtersWithoutDate, dateLabel]
      : filtersWithoutDate
  }

  hasDraftFilterChanges() {
    return (
      this.selectedFilters.length !== this.draftFilters.length ||
      this.selectedFilters.some((filter, index) => filter !== this.draftFilters[index]) ||
      this.dateFilterMode !== this.draftDateFilterMode ||
      this.filterMonth !== this.draftFilterMonth ||
      this.filterDateFrom !== this.draftFilterDateFrom ||
      this.filterDateTo !== this.draftFilterDateTo
    )
  }

  applyFilters() {
    const filtersChanged = this.hasDraftFilterChanges()
    if (!filtersChanged) return

    this.selectedFilters = [...this.draftFilters]
    this.dateFilterMode = this.draftDateFilterMode
    this.filterMonth = this.draftFilterMonth
    this.filterDateFrom = this.draftFilterDateFrom
    this.filterDateTo = this.draftFilterDateTo
    this.activePreset = ''
    this.monthPickerOpen = false
    this.filtersOpen = false
  }

  closePanels() {
    this.filtersOpen = false
    this.sortOpen = false
    this.monthPickerOpen = false
  }

  handleDocumentKeyDown(event) {
    if (event.key !== 'Escape') return
    if (this.monthPickerOpen) {
      this.monthPickerOpen = false
      return
    }
    this.closePanels()
  }

  updatePageScrollLock() {
    const shouldLock = (this.filtersOpen || this.sortOpen) &&
      window.matchMedia('(max-width: 767px)').matches

    if (shouldLock && !this.pageScrollLocked) {
      this.previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      this.pageScrollLocked = true
    } else if (!shouldLock) {
      this.restorePageScroll()
    }
  }

  restorePageScroll() {
    if (!this.pageScrollLocked) return
    document.body.style.overflow = this.previousBodyOverflow || ''
    this.pageScrollLocked = false
  }

  removeHiddenFilters() {
    this.selectedFilters = this.selectedFilters.slice(0, this.visibleFilterCount)
    this.activePreset = ''
  }

  renderPresetFallbackIcon(icon) {
    const icons = {
      women: html`
        <svg class="preset-women-icon" viewBox="0 0 24 24">
          <path d="M5 7a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
          <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path>
        </svg>
      `,
      city: html`
        <svg viewBox="0 0 24 24">
          <path d="M4 21V8h7v13M11 21V4h9v17M2 21h20"></path>
          <path d="M7 11h1M7 15h1M14 8h2M14 12h2M14 16h2"></path>
        </svg>
      `,
      exotic: html`
        <svg viewBox="0 0 24 24">
          <path d="M12 21c1-6 .5-10-2-14M11 9C7 5 4 6 3 7M11 9c0-5 3-6 5-6M11 9c4-3 7-1 8 1M11 9c-3 0-5 2-6 4"></path>
          <path d="M3 21h18"></path>
        </svg>
      `,
      rest: html`
        <svg viewBox="0 0 24 24">
          <circle cx="17" cy="6" r="3"></circle>
          <path d="M3 15c2-1.5 4-1.5 6 0s4 1.5 6 0 4-1.5 6 0M3 19c2-1.5 4-1.5 6 0s4 1.5 6 0 4-1.5 6 0"></path>
        </svg>
      `,
      active: html`
        <svg class="preset-active-compass" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9"></circle>
          <path d="m15.8 8.2-2.4 5.2-5.2 2.4 2.4-5.2 5.2-2.4Z"></path>
          <circle cx="12" cy="12" r="1"></circle>
        </svg>
      `,
      europe: html`
        <svg class="preset-landmark-icon" viewBox="0 0 24 24">
          <path d="M11 4c0 4.889-2.292 12.111-5 17"></path>
          <path d="M13 4c0 4.889 2.292 12.111 5 17"></path>
          <path d="M3 21h18M8 14h8M9 10h6M10 4h4M12 2v1.778"></path>
          <path d="M10 21s.27-1.406.667-2c.333-.5.666-1 1.333-1s1 .5 1.333 1c.448.672.667 2 .667 2"></path>
        </svg>
      `,
      asia: html`
        <svg class="preset-landmark-icon" viewBox="0 0 24 24">
          <path d="M4 4c5.333 1.333 10.667 1.333 16 0"></path>
          <path d="M4 8h16M12 5v3M18 4.5V20M6 4.5V20"></path>
        </svg>
      `,
      shortTrip: html`
        <svg class="preset-luggage-icon" viewBox="0 0 24 24">
          <path d="M5 18v-6a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v6a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3"></path>
          <path d="M10 6V5a2 2 0 1 1 4 0v1M9 21v-4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4M11 10h2"></path>
        </svg>
      `,
      longTrip: html`
        <svg class="preset-luggage-icon" viewBox="0 0 24 24">
          <path d="M6 8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8"></path>
          <path d="M9 6V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1M6 10h12M6 16h12M9 20v1M15 20v1"></path>
        </svg>
      `,
      lastPlaces: html`
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9"></circle>
          <path d="M12 7v5l3 2M12 3V1M8 1h8"></path>
        </svg>
      `,
      winter: html`
        <svg viewBox="0 0 24 24">
          <path d="M12 2v20M4.2 6.5l15.6 11M4.2 17.5l15.6-11"></path>
          <path d="m9.5 4.5 2.5 2.2 2.5-2.2M9.5 19.5l2.5-2.2 2.5 2.2"></path>
          <path d="m4.8 9.7 2.8-.6-.5-2.8M19.2 14.3l-2.8.6.5 2.8"></path>
          <path d="m4.8 14.3 2.8.6-.5 2.8M19.2 9.7l-2.8-.6.5-2.8"></path>
        </svg>
      `,
      spring: html`
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="6" r="3"></circle>
          <circle cx="17.5" cy="10.2" r="3"></circle>
          <circle cx="15.4" cy="16.5" r="3"></circle>
          <circle cx="8.6" cy="16.5" r="3"></circle>
          <circle cx="6.5" cy="10.2" r="3"></circle>
          <circle cx="12" cy="11.5" r="2.2"></circle>
          <path d="M12 18.7V22"></path>
        </svg>
      `,
      summer: html`
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="4"></circle>
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1"></path>
        </svg>
      `,
      autumn: html`
        <svg viewBox="0 0 24 24">
          <path d="M20.5 3.5C13 3.5 6 6.5 5 13.5c-.6 4.2 2.5 7 6.4 7 6.2 0 9.5-6.5 9.1-17Z"></path>
          <path d="M3.5 22c4-6.5 8.5-10.5 14-14"></path>
          <path d="M9.5 15.5c1 .2 2 .3 3.5.1M12.8 11.5c.1 1.2.3 2.2.8 3.1"></path>
        </svg>
      `,
      year: html`
        <svg viewBox="0 0 24 24">
          <rect x="3" y="5" width="18" height="16" rx="3"></rect>
          <path d="M8 3v4M16 3v4M3 10h18M8 14h3M13 14h3M8 18h3"></path>
        </svg>
      `,
    }

    return icons[icon] || icons.city
  }

  getFilterGroupId(label) {
    return FILTER_GROUPS.find(group =>
      group.options.includes(label)
    )?.id ?? ''
  }

  matchesDateFilter(term) {
    if (this.dateFilterMode === 'month') {
      return !this.filterMonth ||
        String(term.startDate).startsWith(this.filterMonth)
    }

    const startDate = String(term.startDate || '')
    const endDate = String(term.endDate || term.startDate || '')

    if (this.filterDateFrom && endDate < this.filterDateFrom) return false
    if (this.filterDateTo && startDate > this.filterDateTo) return false
    return true
  }

  matchesProgramFilter(program, label) {
    const groupId = this.getFilterGroupId(label)

    if (groupId === 'direction') {
      return program.continent === label
    }

    if (groupId === 'trip-type') {
      return program.travelStyles.includes(label)
    }

    return true
  }

  matchesTermFilter(term, label) {
    const groupId = this.getFilterGroupId(label)
    const duration = Number(term.durationDays)
    const price = Number(term.price)

    if (groupId === 'audience' || label === 'Dla kobiet') {
      const group = String(term.group).toLocaleLowerCase('pl-PL')

      if (label === 'Tylko dla kobiet' || label === 'Dla kobiet') {
        return group.includes('kobiet') && !group.includes('mężczyzn')
      }

      return group.includes('kobiet') && group.includes('mężczyzn')
    }

    if (groupId === 'duration') {
      if (!Number.isFinite(duration)) return false
      if (label === 'Do 5 dni') return duration <= 5
      if (label === '6–9 dni') return duration >= 6 && duration <= 9
      return duration >= 10
    }

    if (groupId === 'price') {
      if (!Number.isFinite(price)) return false
      if (label === 'Do 2 999 zł') return price <= 2999
      if (label === '3 000–4 999 zł') return price >= 3000 && price <= 4999
      if (label === '5 000–8 000 zł') return price >= 5000 && price <= 8000
      return price > 8000
    }

    if (groupId === 'availability' || label === 'Mało miejsc') {
      if (label === 'Dostępne miejsca') {
        return term.availabilityVariant === 'available'
      }

      return term.availabilityVariant === 'last-places'
    }

    const seasonMatch = label.match(/^(Zima|Wiosna|Lato|Jesień) (\d{4})$/)
    if (seasonMatch) {
      const seasonByLabel = {
        Zima: 'winter',
        Wiosna: 'spring',
        Lato: 'summer',
        Jesień: 'autumn',
      }

      return String(term.startDate).startsWith(`${seasonMatch[2]}-`) &&
        getSeasonFromStartDate(term.startDate) === seasonByLabel[seasonMatch[1]]
    }

    const yearMatch = label.match(/^Rok (\d{4})$/)
    if (yearMatch) {
      return String(term.startDate).startsWith(`${yearMatch[1]}-`)
    }

    return true
  }

  getAppliedOptionFilters() {
    const dateLabel = this.getDateFilterLabel(
      this.dateFilterMode,
      this.filterMonth,
      this.filterDateFrom,
      this.filterDateTo
    )

    return this.selectedFilters.filter(label => label !== dateLabel)
  }

  groupFilters(filters) {
    const groups = new Map()

    filters.forEach(label => {
      const groupId = this.getFilterGroupId(label) || `preset:${label}`
      const labels = groups.get(groupId) || []
      labels.push(label)
      groups.set(groupId, labels)
    })

    return groups
  }

  programMatchesFilterLabels(
    program,
    filters,
    matchesTerm = () => true
  ) {
    const programFilters = filters.filter(label => {
      const groupId = this.getFilterGroupId(label)
      return groupId === 'direction' || groupId === 'trip-type'
    })
    const termFilters = filters.filter(label =>
      !programFilters.includes(label)
    )
    const programFilterGroups = this.groupFilters(programFilters)
    const termFilterGroups = this.groupFilters(termFilters)

    if (![...programFilterGroups.values()].every(labels =>
      labels.some(label => this.matchesProgramFilter(program, label))
    )) {
      return false
    }

    return program.terms.some(term =>
      matchesTerm(term) &&
      [...termFilterGroups.values()].every(labels =>
        labels.some(label => this.matchesTermFilter(term, label))
      )
    )
  }

  termMatchesFilterLabels(program, term, filters) {
    const filterGroups = this.groupFilters(filters)

    return [...filterGroups.values()].every(labels =>
      labels.some(label =>
        this.matchesProgramFilter(program, label) &&
        this.matchesTermFilter(term, label)
      )
    )
  }

  programMatchesFilters(program) {
    return this.programMatchesFilterLabels(
      program,
      this.getAppliedOptionFilters(),
      term => this.matchesDateFilter(term)
    )
  }

  termMatchesFilters(program, term) {
    return this.matchesDateFilter(term) &&
      this.termMatchesFilterLabels(
        program,
        term,
        this.getAppliedOptionFilters()
      )
  }

  optionHasResults(groupId, label) {
    if (groupId === 'direction' || groupId === 'trip-type') {
      return this.programs.some(program =>
        this.matchesProgramFilter(program, label)
      )
    }

    return this.programs.some(program =>
      program.terms.some(term => this.matchesTermFilter(term, label))
    )
  }

  getAvailableFilterGroups() {
    return FILTER_GROUPS
      .map(group => ({
        ...group,
        options: group.options.filter(option =>
          this.optionHasResults(group.id, option)
        ),
      }))
      .filter(group => group.options.length > 0)
  }

  presetHasResults(preset) {
    const filters = preset.filters || []

    if (this.activeView === 'programs') {
      return this.programs.some(program =>
        this.programMatchesFilterLabels(program, filters)
      )
    }

    return this.programs.some(program =>
      program.terms.some(term =>
        this.termMatchesFilterLabels(program, term, filters)
      )
    )
  }

  sortPrograms(programs) {
    const sorted = [...programs]
    const nearestDate = program => program.terms[0]?.startDate || ''
    const compareRecommended = (first, second) =>
      first.recommendedOrder - second.recommendedOrder

    sorted.sort((first, second) => {
      if (this.sortValue === 'price-low') {
        return (first.priceFrom ?? Infinity) - (second.priceFrom ?? Infinity)
      }
      if (this.sortValue === 'price-high') {
        return (second.priceFrom ?? -Infinity) - (first.priceFrom ?? -Infinity)
      }
      if (this.sortValue === 'shortest') {
        return (first.durationDays ?? Infinity) - (second.durationDays ?? Infinity)
      }
      if (this.sortValue === 'longest') {
        return (second.durationDays ?? -Infinity) - (first.durationDays ?? -Infinity)
      }
      if (this.sortValue === 'nearest') {
        return nearestDate(first).localeCompare(nearestDate(second)) ||
          compareRecommended(first, second)
      }
      if (this.sortValue === 'recently-added') {
        return second.publishedAt.localeCompare(first.publishedAt) ||
          compareRecommended(first, second)
      }

      return compareRecommended(first, second)
    })

    return sorted
  }

  getVisiblePrograms() {
    return this.sortPrograms(
      this.programs.filter(program => this.programMatchesFilters(program))
    )
  }

  getVisibleCalendarTerms() {
    return this.programs.flatMap(program =>
      program.terms
        .filter(term => this.termMatchesFilters(program, term))
        .map(term => ({ program, term }))
    )
  }

  sortCalendarTermsWithinMonth(items) {
    return [...items].sort((first, second) => {
      const chronologicalFallback =
        first.term.startDate.localeCompare(second.term.startDate)

      if (this.sortValue === 'latest') {
        return second.term.startDate.localeCompare(first.term.startDate)
      }
      if (this.sortValue === 'price-low') {
        return (first.term.price ?? Infinity) - (second.term.price ?? Infinity) ||
          chronologicalFallback
      }
      if (this.sortValue === 'price-high') {
        return (second.term.price ?? -Infinity) - (first.term.price ?? -Infinity) ||
          chronologicalFallback
      }
      if (this.sortValue === 'shortest') {
        return (first.term.durationDays ?? Infinity) -
          (second.term.durationDays ?? Infinity) ||
          chronologicalFallback
      }
      if (this.sortValue === 'longest') {
        return (second.term.durationDays ?? -Infinity) -
          (first.term.durationDays ?? -Infinity) ||
          chronologicalFallback
      }
      if (this.sortValue === 'recently-added') {
        return second.program.publishedAt.localeCompare(first.program.publishedAt) ||
          chronologicalFallback
      }

      return chronologicalFallback
    })
  }

  getCalendarMonthEntries(items = this.getVisibleCalendarTerms()) {
    const termsByMonth = new Map()

    items.forEach(item => {
      const monthKey = item.term.startDate.slice(0, 7)
      const monthTerms = termsByMonth.get(monthKey) || []
      monthTerms.push(item)
      termsByMonth.set(monthKey, monthTerms)
    })

    return [...termsByMonth.entries()]
      .sort(([firstMonth], [secondMonth]) =>
        this.sortValue === 'latest'
          ? secondMonth.localeCompare(firstMonth)
          : firstMonth.localeCompare(secondMonth)
      )
      .map(([monthKey, monthTerms]) => [
        monthKey,
        this.sortCalendarTermsWithinMonth(monthTerms),
      ])
  }

  renderPresets() {
    const configuredPresets = this.activeView === 'programs'
      ? programPresets
      : calendarPresets
    const presets = configuredPresets.filter(preset =>
      this.presetHasResults(preset)
    )

    return html`
      <div
        class="presets"
        style=${`--preset-count: ${Math.max(1, Math.min(6, presets.length))}`}
      >
        ${presets.map(preset => html`
          <button
            class="preset"
            type="button"
            aria-pressed=${this.activePreset === preset.label}
            @click=${() => this.selectPreset(preset)}
          >
            <span class="preset-icon" aria-hidden="true">
              ${this.renderPresetFallbackIcon(preset.icon)}
              ${preset.iconUrl
                ? html`
                    <img
                      class="preset-custom-icon"
                      src=${preset.iconUrl}
                      alt=""
                      @error=${event => { event.currentTarget.hidden = true }}
                    >
                  `
                : ''}
            </span>
            <span class="preset-label">${preset.label}</span>
          </button>
        `)}
      </div>
    `
  }

  renderDateFilter() {
    const availableMonths = this.getAvailableMonthValues()

    return html`
      <section class="filter-group filter-group--date">
        <div class="filter-group-heading">
          <h4>Termin wyjazdu</h4>
        </div>
        <div class="date-filter-layout">
          <div class="date-mode" role="group" aria-label="Sposób wyboru terminu">
            <button
              type="button"
              aria-pressed=${this.draftDateFilterMode === 'month'}
              @click=${() => this.setDateFilterMode('month')}
            >Miesiąc</button>
            <button
              type="button"
              aria-pressed=${this.draftDateFilterMode === 'range'}
              @click=${() => this.setDateFilterMode('range')}
            >Zakres dat</button>
          </div>

          ${this.draftDateFilterMode === 'month'
            ? html`
                <div class="date-field month-picker-custom">
                  <div class="month-picker-control" data-has-value=${Boolean(this.draftFilterMonth)}>
                    <button
                      class="month-picker-trigger"
                      type="button"
                      aria-expanded=${this.monthPickerOpen}
                      aria-haspopup="dialog"
                      @click=${this.toggleMonthPicker}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="3" y="5" width="18" height="16" rx="3"></rect>
                        <path d="M8 3v4M16 3v4M3 10h18"></path>
                      </svg>
                      <span class=${this.draftFilterMonth ? '' : 'month-placeholder'}>
                        ${formatSelectedMonth(this.draftFilterMonth) || 'Wybierz miesiąc'}
                      </span>
                      ${this.draftFilterMonth
                        ? html`<span class="month-clear-space" aria-hidden="true"></span>`
                        : ''}
                      <svg class="month-picker-chevron" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="m4 6 4 4 4-4"></path>
                      </svg>
                    </button>
                    ${this.draftFilterMonth
                      ? html`
                          <button
                            class="month-picker-clear"
                            type="button"
                            aria-label="Wyczyść wybrany miesiąc"
                            @click=${this.clearMonthSelection}
                          >×</button>
                        `
                      : ''}
                  </div>

                  ${this.monthPickerOpen
                    ? html`
                        <div class="month-picker-popover" role="dialog" aria-label="Wybierz miesiąc">
                          <div class="month-picker-header">
                            <button
                              type="button"
                              aria-label="Poprzedni rok"
                              ?disabled=${!this.canChangeMonthPickerYear(-1)}
                              @click=${() => this.changeMonthPickerYear(-1)}
                            >‹</button>
                            <strong>${this.monthPickerYear}</strong>
                            <button
                              type="button"
                              aria-label="Następny rok"
                              ?disabled=${!this.canChangeMonthPickerYear(1)}
                              @click=${() => this.changeMonthPickerYear(1)}
                            >›</button>
                          </div>
                          <div class="month-picker-grid">
                            ${MONTH_OPTIONS.map((month, index) => {
                              const value = `${this.monthPickerYear}-${String(index + 1).padStart(2, '0')}`
                              return html`
                                <button
                                  type="button"
                                  aria-pressed=${this.draftFilterMonth === value}
                                  ?disabled=${!availableMonths.has(value)}
                                  @click=${() => this.selectMonth(index)}
                                >${month}</button>
                              `
                            })}
                          </div>
                        </div>
                      `
                    : ''}
                </div>

                <label
                  class="date-field month-picker-native"
                  data-has-value=${Boolean(this.draftFilterMonth)}
                >
                  <span class="native-month-label">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <rect x="3" y="5" width="18" height="16" rx="3"></rect>
                      <path d="M8 3v4M16 3v4M3 10h18"></path>
                    </svg>
                    ${this.draftFilterMonth ? '' : html`<span>Wybierz miesiąc</span>`}
                  </span>
                  <input
                    type="month"
                    aria-label="Wybierz miesiąc"
                    .value=${this.draftFilterMonth}
                    @change=${event => this.updateDateFilter('draftFilterMonth', event.target.value)}
                  >
                </label>
              `
            : html`
                <div class="date-range">
                  <label class="date-field date-field--inside">
                    <span>Od:</span>
                    <input
                      type="date"
                      aria-label="Data od"
                      .value=${this.draftFilterDateFrom}
                      @change=${event => this.updateDateFilter('draftFilterDateFrom', event.target.value)}
                    >
                  </label>
                  <label class="date-field date-field--inside">
                    <span>Do:</span>
                    <input
                      type="date"
                      aria-label="Data do"
                      .value=${this.draftFilterDateTo}
                      @change=${event => this.updateDateFilter('draftFilterDateTo', event.target.value)}
                    >
                  </label>
                </div>
              `}
        </div>
      </section>
    `
  }

  renderFilterGroup(group) {
    const singleChoice = group.selection === 'single'

    return html`
      <section class="filter-group">
        <div class="filter-group-heading">
          <h4>${group.title}</h4>
        </div>
        <div class="filter-choices" role=${singleChoice ? 'radiogroup' : 'group'}>
          ${group.options.map(label => {
            const selected = this.draftFilters.includes(label)
            return html`
              <button
                class="filter-choice"
                type="button"
                role=${singleChoice ? 'radio' : 'checkbox'}
                aria-checked=${selected}
                @click=${() => this.toggleFilter(label, group)}
              >
                <span class="choice-control" aria-hidden="true">
                  ${selected
                    ? html`<svg viewBox="0 0 16 16"><path d="m3.5 8 2.8 2.8 6.2-6.3"></path></svg>`
                    : ''}
                </span>
                <span>${label}</span>
              </button>
            `
          })}
        </div>
      </section>
    `
  }

  handleDocumentPointerDown(event) {
    if (!this.filtersOpen && !this.sortOpen) return

    const eventPath = event.composedPath()
    const monthPicker = this.renderRoot.querySelector('.month-picker-popover')
    const monthPickerTrigger = this.renderRoot.querySelector('.month-picker-trigger')
    const filterButton = this.renderRoot.querySelector('.filter-button')
    const filterPanel = this.renderRoot.querySelector('.filter-panel')
    const sortButton = this.renderRoot.querySelector('.sort-button')
    const sortPanel = this.renderRoot.querySelector('.sort-panel')

    if (
      this.monthPickerOpen &&
      !eventPath.includes(monthPicker) &&
      !eventPath.includes(monthPickerTrigger)
    ) {
      this.monthPickerOpen = false
    }

    if (
      this.filtersOpen &&
      !eventPath.includes(filterButton) &&
      !eventPath.includes(filterPanel)
    ) {
      this.filtersOpen = false
    }

    if (
      this.sortOpen &&
      !eventPath.includes(sortButton) &&
      !eventPath.includes(sortPanel)
    ) {
      this.sortOpen = false
    }
  }

  measureVisibleFilters() {
    const chips = this.renderRoot.querySelector('.chips')
    if (!chips || !this.selectedFilters.length) {
      if (this.visibleFilterCount !== 0) this.visibleFilterCount = 0
      return
    }

    const availableWidth = chips.clientWidth
    if (!availableWidth) return

    const chipWidths = [...this.renderRoot.querySelectorAll('[data-measure-chip]')]
      .map(element => element.getBoundingClientRect().width)
    const gap = 7
    const fullWidth = chipWidths.reduce((sum, width) => sum + width, 0) +
      gap * Math.max(0, chipWidths.length - 1)

    let nextVisibleCount = chipWidths.length

    if (fullWidth > availableWidth) {
      nextVisibleCount = 0

      for (let visibleCount = chipWidths.length - 1; visibleCount >= 0; visibleCount -= 1) {
        const hiddenCount = chipWidths.length - visibleCount
        const more = this.renderRoot.querySelector(`[data-measure-more="${hiddenCount}"]`)
        const moreWidth = more?.getBoundingClientRect().width || 0
        const visibleWidth = chipWidths
          .slice(0, visibleCount)
          .reduce((sum, width) => sum + width, 0)
        const elementCount = visibleCount + 1
        const requiredWidth = visibleWidth + moreWidth + gap * Math.max(0, elementCount - 1)

        if (requiredWidth <= availableWidth) {
          nextVisibleCount = visibleCount
          break
        }
      }
    }

    if (nextVisibleCount !== this.visibleFilterCount) {
      this.visibleFilterCount = nextVisibleCount
    }
  }

  renderToolbar() {
    const programsView = this.activeView === 'programs'
    const visibleFilters = this.selectedFilters.slice(0, this.visibleFilterCount)
    const hiddenFilterCount = Math.max(
      0,
      this.selectedFilters.length - this.visibleFilterCount
    )
    const sortOptions = programsView
      ? PROGRAM_SORT_OPTIONS
      : TERM_SORT_OPTIONS
    const activeSortLabel = sortOptions.find(
      option => option.value === this.sortValue
    )?.label || sortOptions[0].label

    return html`
      <div class="toolbar-wrap">
        <div
          class="toolbar"
          data-has-filters=${this.selectedFilters.length > 0}
        >
          <div class="filter-area">
            <button
              class="filter-button"
              type="button"
              aria-expanded=${this.filtersOpen}
              data-has-filters=${this.selectedFilters.length > 0}
              @click=${this.toggleFiltersPanel}
            >
              <span class="filter-button-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M4 6h10M18 6h2M4 12h3M11 12h9M4 18h8M16 18h4"></path>
                  <circle cx="16" cy="6" r="2"></circle>
                  <circle cx="9" cy="12" r="2"></circle>
                  <circle cx="14" cy="18" r="2"></circle>
                </svg>
              </span>
              Filtry
              ${this.selectedFilters.length
                ? html`<span class="filter-count">${this.selectedFilters.length}</span>`
                : ''}
            </button>

            <div
              class="chips"
              aria-label="Wybrane filtry"
              data-has-filters=${this.selectedFilters.length > 0}
            >
              ${visibleFilters.map(label => html`
                <span class="chip">
                  ${label}
                  <button
                    class="chip-remove"
                    type="button"
                    aria-label="Usuń filtr ${label}"
                    @click=${() => this.removeFilter(label)}
                  >×</button>
                </span>
              `)}
              ${hiddenFilterCount
                ? html`
                    <span class="chip chip--more">
                      +${hiddenFilterCount} więcej
                      <button
                        class="chip-remove"
                        type="button"
                        aria-label="Usuń pozostałe filtry"
                        @click=${this.removeHiddenFilters}
                      >×</button>
                    </span>
                  `
                : ''}
            </div>
            <div class="chip-measurer" aria-hidden="true">
              ${this.selectedFilters.map((label, index) => html`
                <span class="chip" data-measure-chip=${index}>
                  ${label}<span class="chip-remove">×</span>
                </span>
              `)}
              ${this.selectedFilters.map((_, index) => html`
                <span class="chip chip--more" data-measure-more=${index + 1}>
                  +${index + 1} więcej<span class="chip-remove">×</span>
                </span>
              `)}
            </div>
          </div>

          <button
            class="sort-button"
            type="button"
            aria-expanded=${this.sortOpen}
            aria-haspopup="listbox"
            @click=${() => {
              this.sortOpen = !this.sortOpen
              this.filtersOpen = false
            }}
          >
            <span class="sort-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M8 4v16M5 7l3-3 3 3M16 20V4M13 17l3 3 3-3"></path>
              </svg>
            </span>
            <span class="sort-label">Sortuj:</span>
            <span class="sort-value">${activeSortLabel}</span>
            <svg class="sort-chevron" viewBox="0 0 16 16" aria-hidden="true">
              <path d="m4 6 4 4 4-4"></path>
            </svg>
          </button>
        </div>

        ${this.filtersOpen || this.sortOpen
          ? html`<div class="panel-backdrop" aria-hidden="true"></div>`
          : ''}

        ${this.filtersOpen
          ? html`
              <aside class="filter-panel" role="dialog" aria-modal="true" aria-labelledby="catalog-filter-title">
                <header class="panel-header">
                  <div class="panel-heading-copy">
                    <h3 class="filter-panel-title" id="catalog-filter-title">Filtry</h3>
                  </div>
                  <button class="panel-close" type="button" aria-label="Zamknij filtry" @click=${this.closePanels}>×</button>
                </header>
                <div class="filter-panel-scroll">
                  ${this.renderDateFilter()}
                  <div class="filter-groups-grid">
                    ${this.getAvailableFilterGroups().map(
                      group => this.renderFilterGroup(group)
                    )}
                  </div>
                </div>
                <footer class="panel-footer">
                  <button
                    class="panel-secondary"
                    type="button"
                    ?disabled=${this.draftFilters.length === 0}
                    @click=${this.clearDraftFilters}
                  >Wyczyść filtry</button>
                  <button
                    class="panel-primary"
                    type="button"
                    ?disabled=${!this.hasDraftFilterChanges()}
                    @click=${this.applyFilters}
                  >Pokaż wyniki (${this.draftFilters.length})</button>
                </footer>
              </aside>
            `
          : ''}

        ${this.sortOpen
          ? html`
              <aside class="sort-panel" role="dialog" aria-modal="true" aria-labelledby="catalog-sort-title">
                <header class="panel-header">
                  <div class="panel-heading-copy">
                    <h3 class="sort-panel-title" id="catalog-sort-title">Sortowanie</h3>
                  </div>
                  <button class="panel-close" type="button" aria-label="Zamknij sortowanie" @click=${this.closePanels}>×</button>
                </header>
                <div class="sort-options" role="listbox" aria-label="Sposób sortowania">
                  ${sortOptions.map(option => html`
                    <button
                      class="sort-option"
                      type="button"
                      role="option"
                      aria-selected=${this.sortValue === option.value}
                      @click=${() => {
                        this.sortValue = option.value
                        this.sortOpen = false
                      }}
                    >
                      <span class="sort-radio" aria-hidden="true"></span>
                      <span>${option.label}</span>
                    </button>
                  `)}
                </div>
              </aside>
            `
          : ''}
      </div>
    `
  }

  renderPrograms() {
    const programs = this.getVisiblePrograms()

    return html`
      <div class="results-heading results-heading--offset">
        <h2>
          Programy
          <span class="results-count" aria-label="Liczba programów">
            ${formatResultCount(programs.length)}
          </span>
        </h2>
      </div>
      ${this.catalogError
        ? html`<p class="catalog-status catalog-status--warning" role="status">${this.catalogError}</p>`
        : ''}
      ${this.catalogLoading
        ? html`<p class="catalog-status" role="status">Ładujemy programy…</p>`
        : programs.length === 0
          ? html`<p class="catalog-status" role="status">Brak programów spełniających wybrane kryteria.</p>`
          : ''}
      <div class="program-grid">
        ${programs.map(program => html`
          <ct-program-card .program=${program}></ct-program-card>
        `)}
      </div>
    `
  }

  renderCalendar() {
    const calendarTerms = this.getVisibleCalendarTerms()
    const monthEntries = this.getCalendarMonthEntries(calendarTerms)

    return html`
      <div class="results-heading results-heading--offset results-heading--calendar">
        <h2>
          Terminy
          <span class="results-count" aria-label="Liczba terminów">
            ${formatResultCount(calendarTerms.length)}
          </span>
        </h2>
      </div>

      ${this.catalogError
        ? html`<p class="catalog-status catalog-status--warning" role="status">${this.catalogError}</p>`
        : ''}
      ${this.catalogLoading
        ? html`<p class="catalog-status" role="status">Ładujemy terminy…</p>`
        : calendarTerms.length === 0
          ? html`<p class="catalog-status" role="status">Brak terminów spełniających wybrane kryteria.</p>`
          : ''}

      ${monthEntries.map(([monthKey, items]) => html`
        <section class="month-group">
          <h3 class="month-heading">
            ${formatMonthHeading(monthKey)}
            <span class="month-count">${formatTermCount(items.length)}</span>
          </h3>
          <div class="term-list">
            ${items.map(({ program, term }) => html`
              <ct-catalog-term-card
                .program=${program}
                .term=${term}
              ></ct-catalog-term-card>
            `)}
          </div>
        </section>
      `)}
    `
  }

  render() {
    const programsView = this.activeView === 'programs'

    return html`
      <main class="catalog">
        <nav class="view-switch" role="tablist" aria-label="Widok katalogu">
          <button
            class="view-button"
            type="button"
            role="tab"
            aria-selected=${!programsView}
            @click=${() => this.changeView('calendar')}
          >
            <span class="view-icon" aria-hidden="true">
              <svg viewBox="0 0 32 32">
                <rect x="4" y="6.5" width="24" height="21" rx="4"></rect>
                <path d="M10 4.5v5M22 4.5v5M4 13h24"></path>
                <path d="M9 18h3M15 18h3M21 18h2M9 23h3M15 23h3"></path>
              </svg>
            </span>
            <span class="view-copy">
              <strong>Terminy</strong>
              <small>Szukaj według daty</small>
            </span>
          </button>
          <button
            class="view-button"
            type="button"
            role="tab"
            aria-selected=${programsView}
            @click=${() => this.changeView('programs')}
          >
            <span class="view-icon" aria-hidden="true">
              <svg class="program-photo-pin-icon" viewBox="0 0 24 24">
                <path stroke="none" d="M0 0h24v24H0z"></path>
                <path d="M15 8h.01"></path>
                <path d="M12.5 21H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v5.5"></path>
                <path d="m3 16 5-5c.928-.893 2.072-.893 3 0l2.5 2.5"></path>
                <path d="M21.121 20.121a3 3 0 1 0-4.242 0c.418.419 1.125 1.045 2.121 1.879 1.051-.89 1.759-1.516 2.121-1.879"></path>
                <path d="M19 18v.01"></path>
              </svg>
            </span>
            <span class="view-copy">
              <strong>Programy</strong>
              <small>Przeglądaj wyjazdy</small>
            </span>
          </button>
        </nav>

        <section class="quick-section">
          <h2 class="quick-title">Szybki wybór</h2>
          ${this.renderPresets()}
        </section>

        ${this.renderToolbar()}

        <section role="tabpanel">
          ${programsView
            ? this.renderPrograms()
            : this.renderCalendar()}
        </section>
      </main>
    `
  }
}

if (!customElements.get('ct-offer-catalog')) {
  customElements.define('ct-offer-catalog', CtOfferCatalog)
}
